import tabs from "assets/json/tabs.json";
import searchForTab from "common/searchTab";

const WindowState = Object.freeze({
	CLOSED: Symbol(0),
	HALF_OPEN: Symbol(1),
	OPEN: Symbol(2),
	isOpen: state => [WindowState.OPEN, WindowState.HALF_OPEN].includes(state)
});

const windowReducerInitialState = {
	activeTabId: 0,
	currentAddress: new URLSearchParams(window.location.search).get("w") || "",
	history: [],
	additionalInformation: null,
	height: 0
};

[
	windowReducerInitialState.sidebarEnabled,
	windowReducerInitialState.tabBarEnabled,
	windowReducerInitialState.open
] = initNavState(windowReducerInitialState.currentAddress);

/**
 * @returns {[boolean, boolean, WindowState]} Whether the sidebar and tab bar should be enabled by default.
 */
function initNavState(address) {
	if (address === "") return [true, true, WindowState.CLOSED];
	return address
		.split(".")
		.reduce((acc, _, i, a) => {
			const tab = searchForTab(tabs, a.slice(0, i + 1).join("."));
			acc.push(tab);
			return acc;
		}, [])
		.reduce(
			(acc, tab) => {
				if (tab?.disableSidebar != null) {
					acc[0] = !tab.disableSidebar;
				}

				if (tab?.disableTabBar != null) {
					acc[1] = !tab.disableTabBar;
				}

				if (tab?.halfOpened === true) {
					acc[2] = WindowState.HALF_OPEN;
				}

				return acc;
			},
			[true, true, WindowState.OPEN]
		);
}

const windowReducer = (state = windowReducerInitialState, action) => {
	const newObject = state;

	if (action.type === "SET_WINDOW_ADDRESS") {
		let newAddress = action.state || "";
		let tab = searchForTab(tabs, newAddress);
		let tabBarEnabled = tab?.disableTabBar == null ? null : !tab.disableTabBar;
		let sidebarEnabled = tab?.disableSidebar == null ? null : !tab.disableSidebar;
		let openedState = tab?.halfOpened === true ? WindowState.HALF_OPEN : null;

		while (tab?.needsSubTab) {
			newAddress = newAddress.concat(".", tab.subTabs[0].id);
			tab = searchForTab(tabs, newAddress);
			tabBarEnabled = tab?.disableTabBar == null ? tabBarEnabled : !tab.disableTabBar;
			sidebarEnabled = tab?.disableSidebar == null ? sidebarEnabled : !tab.disableSidebar;
			openedState = tab?.halfOpened === true ? WindowState.HALF_OPEN : openedState;
		}

		const searchParams = new URLSearchParams(window.location.search);
		searchParams.set("w", newAddress);
		const l = window.location;
		const newUrl = l.origin + l.pathname + "?" + searchParams.toString() + l.hash;
		window.history.pushState({}, "", newUrl);

		if (sidebarEnabled == null || tabBarEnabled == null) {
			const splitted = action.state.split(".");
			splitted.pop();

			while (splitted.length > 0) {
				const parentTab = searchForTab(tabs, splitted.join("."));
				tabBarEnabled ??= parentTab?.disableTabBar == null ? null : !parentTab.disableTabBar;
				sidebarEnabled ??= parentTab?.disableSidebar == null ? null : !parentTab.disableSidebar;
				parentTab?.halfOpened && (openedState = WindowState.HALF_OPEN);
				splitted.pop();
			}
		}

		tabBarEnabled ??= true;
		sidebarEnabled ??= true;
		openedState ??= newAddress === "" ? WindowState.CLOSED : WindowState.OPEN;
		return {
			...state,
			open: openedState,
			currentAddress: newAddress,
			history: action.preserveHistory === false ? state.history : [...state.history, state.currentAddress],
			additionalInformation:
				action.additionalInformation === undefined ? state.additionalInformation : action.additionalInformation,
			sidebarEnabled: sidebarEnabled,
			tabBarEnabled: tabBarEnabled
		};
	}

	if (action.type === "SET_WINDOW_HISTORY") {
		return { ...state, history: action.state };
	}

	if (action.type === "SET_WINDOW_SIDEBAR_ENABLED") {
		return { ...state, sidebarEnabled: action.payload || !state.sidebarEnabled };
	}

	if (action.type === "SET_WINDOW_TAB_BAR_ENABLED") {
		return { ...state, tabBarEnabled: action.payload || !state.tabBarEnabled };
	}

	if (action.type === "SET_WINDOW_HEIGHT") {
		newObject.height = action.state;
		return newObject;
	}

	if (action.type === "WINDOW_SET") {
		return action.state;
	}

	return state;
};

export default windowReducer;
export { windowReducer, WindowState };
